import React, { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { superAdminAPI } from '../../lib/walletApi';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import {
  Globe, Shield, Webhook, Mail, Users, CheckCircle, AlertTriangle,
  Save, Loader2, Activity, Lock, RefreshCw, Eye, EyeOff
} from 'lucide-react';
import { toast } from 'sonner';

const SECTIONS = [
  { id: 'environment', label: 'Environment Status', icon: Activity },
  { id: 'mfb-config', label: 'Assets MFB Configuration', icon: Globe },
  { id: 'proxy-config', label: 'Fixie Proxy Configuration', icon: Shield },
  { id: 'webhook-config', label: 'Webhook Configuration', icon: Webhook },
  { id: 'email-config', label: 'Email Configuration', icon: Mail },
  { id: 'alert-recipients', label: 'Alert Recipients', icon: Users },
  { id: 'production-readiness', label: 'Production Readiness Checklist', icon: CheckCircle },
  { id: 'danger-zone', label: 'Danger Zone', icon: AlertTriangle, danger: true },
];

// Reusable field component
function ConfigField({ label, fieldKey, fields, edits, setEdits, sensitive = false }) {
  const f = fields?.[fieldKey] || {};
  const [visible, setVisible] = useState(false);
  const val = edits[fieldKey] !== undefined ? edits[fieldKey] : (f.value || '');
  const changed = edits[fieldKey] !== undefined && edits[fieldKey] !== f.value;

  return (
    <div className="space-y-1" data-testid={`config-field-${fieldKey}`}>
      <div className="flex items-center justify-between">
        <label className="text-xs font-medium text-gray-700">{label}</label>
        <div className="flex items-center gap-2">
          {f.source && (
            <span className={`text-[9px] font-semibold px-1.5 py-0.5 rounded ${
              f.source === 'DATABASE' ? 'bg-blue-50 text-blue-600' :
              f.source === 'ENV_VAR' ? 'bg-purple-50 text-purple-600' : 'bg-gray-100 text-gray-500'
            }`}>{f.source}</span>
          )}
          {f.updated_at && <span className="text-[9px] text-gray-400">{new Date(f.updated_at).toLocaleDateString()}</span>}
        </div>
      </div>
      <div className="relative">
        <input
          type={sensitive && !visible ? 'password' : 'text'}
          value={val}
          onChange={e => setEdits(prev => ({ ...prev, [fieldKey]: e.target.value }))}
          placeholder={f.source === 'NOT_SET' ? 'Not configured' : 'Enter to update'}
          className={`w-full px-3 py-2 text-sm font-mono border rounded-lg pr-10 focus:outline-none focus:ring-2 focus:ring-pink-300 ${
            changed ? 'border-pink-300 bg-pink-50/30' : 'border-gray-200'
          }`}
          data-testid={`input-${fieldKey}`}
        />
        {sensitive && (
          <button onClick={() => setVisible(!visible)} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
            {visible ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
          </button>
        )}
      </div>
    </div>
  );
}

export default function SettingsPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const activeSection = searchParams.get('section') || 'environment';
  const [cs, setCs] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [edits, setEdits] = useState({});

  const loadData = async () => {
    setLoading(true);
    try {
      const data = await superAdminAPI.getConfigStatus();
      setCs(data);
      setEdits({});
    } catch {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadData(); }, []);

  const saveAll = async () => {
    const changedKeys = Object.keys(edits).filter(k => edits[k] !== (cs?.fields?.[k]?.value || ''));
    if (!changedKeys.length) { toast.info('No changes to save'); return; }
    setSaving(true);
    setSaved(false);
    try {
      const items = changedKeys.map(k => ({ key: k, value: edits[k] }));
      const result = await superAdminAPI.updateSystemConfig(items);
      toast.success(`Saved ${result.updated_keys?.length || 0} settings! Active URL: ${result.active_url}`);
      setSaved(true);
      await loadData();
      setTimeout(() => setSaved(false), 5000);
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  const changedCount = Object.keys(edits).filter(k => edits[k] !== (cs?.fields?.[k]?.value || '')).length;
  const fields = cs?.fields || {};

  if (loading) {
    return <div className="flex items-center justify-center py-20"><Loader2 className="w-6 h-6 animate-spin text-pink-500" /></div>;
  }

  const SaveBar = () => changedCount > 0 ? (
    <div className="sticky bottom-0 bg-white border-t border-gray-200 p-3 flex items-center justify-between rounded-b-lg shadow-lg z-10">
      <span className="text-sm text-gray-600">{changedCount} unsaved change{changedCount > 1 ? 's' : ''}</span>
      <div className="flex items-center gap-3">
        <Button variant="outline" size="sm" onClick={() => setEdits({})} className="h-8 text-xs">Discard</Button>
        <Button size="sm" className="bg-pink-500 hover:bg-pink-600 text-white h-8 text-xs" onClick={saveAll} disabled={saving} data-testid="settings-save-btn">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1.5" /> : <Save className="w-3.5 h-3.5 mr-1.5" />}
          {saving ? 'Saving...' : 'Save All'}
        </Button>
        {saved && <span className="text-xs text-emerald-600 font-medium flex items-center gap-1"><CheckCircle className="w-3.5 h-3.5" /> Saved!</span>}
      </div>
    </div>
  ) : null;

  return (
    <div className="flex gap-6" data-testid="settings-page">
      {/* Secondary Nav */}
      <div className="w-56 shrink-0 hidden lg:block">
        <div className="sticky top-20 space-y-0.5">
          <p className="text-[10px] font-semibold text-gray-400 uppercase tracking-wider px-3 pb-2">Settings</p>
          {SECTIONS.map(s => (
            <button key={s.id} onClick={() => setSearchParams({ section: s.id })} data-testid={`settings-nav-${s.id}`}
              className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-all ${
                activeSection === s.id ? 'bg-pink-50 text-pink-600 font-medium'
                : s.danger ? 'text-red-500 hover:bg-red-50' : 'text-gray-600 hover:bg-gray-50'
              }`}>
              <s.icon className="w-4 h-4" /><span>{s.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 space-y-6">
        <div className="lg:hidden">
          <select value={activeSection} onChange={e => setSearchParams({ section: e.target.value })}
            className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg">
            {SECTIONS.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
          </select>
        </div>

        {/* ===== Environment Status ===== */}
        {activeSection === 'environment' && cs && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Environment Status</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Card className="border-gray-200"><CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cs.environment === 'PRODUCTION' ? 'bg-emerald-100' : 'bg-amber-100'}`}>
                    <Activity className={`w-5 h-5 ${cs.environment === 'PRODUCTION' ? 'text-emerald-600' : 'text-amber-600'}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Environment</p>
                    <p className={`text-lg font-bold ${cs.environment === 'PRODUCTION' ? 'text-emerald-600' : 'text-amber-600'}`} data-testid="settings-env-label">{cs.environment}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1.5 border-b border-gray-50"><span className="text-gray-500">Active URL</span><span className="font-mono text-xs text-right break-all" data-testid="settings-active-url">{cs.active_url}</span></div>
                  <div className="flex justify-between py-1.5"><span className="text-gray-500">URL Source</span><span className={`text-xs font-semibold px-2 py-0.5 rounded ${cs.url_source === 'DATABASE' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>{cs.url_source}</span></div>
                </div>
              </CardContent></Card>
              <Card className="border-gray-200"><CardContent className="p-5">
                <div className="flex items-center gap-3 mb-4">
                  <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${cs.proxy_active ? 'bg-emerald-100' : 'bg-red-100'}`}>
                    <Shield className={`w-5 h-5 ${cs.proxy_active ? 'text-emerald-600' : 'text-red-600'}`} />
                  </div>
                  <div>
                    <p className="text-xs text-gray-500">Egress Proxy</p>
                    <p className={`text-lg font-bold ${cs.proxy_active ? 'text-emerald-600' : 'text-red-600'}`}>{cs.proxy_active ? 'Active' : 'Inactive'}</p>
                  </div>
                </div>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between py-1.5 border-b border-gray-50"><span className="text-gray-500">Outbound IP</span><span className="font-mono text-xs" data-testid="settings-outbound-ip">{cs.outbound_ip}</span></div>
                  <div className="flex justify-between py-1.5"><span className="text-gray-500">Fixie Match</span><span className={`text-xs font-semibold ${cs.ip_matches_fixie ? 'text-emerald-600' : 'text-red-600'}`}>{cs.ip_matches_fixie ? `Yes (${cs.fixie_environment})` : 'No'}</span></div>
                </div>
              </CardContent></Card>
            </div>
          </div>
        )}

        {/* ===== Assets MFB Configuration ===== */}
        {activeSection === 'mfb-config' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Assets MFB Configuration</h2>
            {cs && (
              <div className="p-3 rounded-lg bg-gray-50 border border-gray-100 text-sm">
                <span className="text-gray-500">Active: </span>
                <span className="font-mono font-bold text-gray-900">{cs.active_url}</span>
                <span className={`ml-2 text-[10px] font-semibold px-1.5 py-0.5 rounded ${cs.url_source === 'DATABASE' ? 'bg-blue-50 text-blue-600' : 'bg-purple-50 text-purple-600'}`}>{cs.url_source}</span>
              </div>
            )}
            <Card className="border-emerald-200"><CardContent className="p-5 space-y-4">
              <p className="text-xs font-bold text-emerald-700 uppercase tracking-wider flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Production Credentials</p>
              <ConfigField label="Production URL" fieldKey="ASSETS_MFB_BASE_URL_PRODUCTION" fields={fields} edits={edits} setEdits={setEdits} />
              <ConfigField label="Production Client ID" fieldKey="ASSETS_MFB_CLIENT_ID_PRODUCTION" fields={fields} edits={edits} setEdits={setEdits} sensitive />
              <ConfigField label="Production Secret" fieldKey="ASSETS_MFB_SECRET_PRODUCTION" fields={fields} edits={edits} setEdits={setEdits} sensitive />
              <ConfigField label="Production Webhook Secret" fieldKey="ASSETS_MFB_WEBHOOK_SECRET_PRODUCTION" fields={fields} edits={edits} setEdits={setEdits} sensitive />
            </CardContent></Card>
            <Card className="border-amber-200"><CardContent className="p-5 space-y-4">
              <p className="text-xs font-bold text-amber-700 uppercase tracking-wider flex items-center gap-2"><Lock className="w-3.5 h-3.5" /> Staging Credentials</p>
              <ConfigField label="Staging URL" fieldKey="ASSETS_MFB_BASE_URL_STAGING" fields={fields} edits={edits} setEdits={setEdits} />
              <ConfigField label="Staging Client ID" fieldKey="ASSETS_MFB_CLIENT_ID_STAGING" fields={fields} edits={edits} setEdits={setEdits} sensitive />
              <ConfigField label="Staging Secret" fieldKey="ASSETS_MFB_SECRET_STAGING" fields={fields} edits={edits} setEdits={setEdits} sensitive />
            </CardContent></Card>
            <SaveBar />
          </div>
        )}

        {/* ===== Fixie Proxy Configuration ===== */}
        {activeSection === 'proxy-config' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Fixie Proxy Configuration</h2>
            <Card className="border-gray-200"><CardContent className="p-5 space-y-4">
              <ConfigField label="Production Fixie URL" fieldKey="FIXIE_URL_PRODUCTION" fields={fields} edits={edits} setEdits={setEdits} sensitive />
              <ConfigField label="Staging Fixie URL" fieldKey="FIXIE_URL_STAGING" fields={fields} edits={edits} setEdits={setEdits} sensitive />
              {cs && (
                <div className="grid grid-cols-2 gap-3 mt-3">
                  <div className="bg-amber-50 rounded-lg p-3">
                    <p className="text-[10px] text-amber-600 uppercase font-medium mb-1">Staging IPs</p>
                    {cs.known_fixie_ips?.staging?.map(ip => <p key={ip} className="text-xs font-mono text-gray-900">{ip}</p>)}
                  </div>
                  <div className="bg-emerald-50 rounded-lg p-3">
                    <p className="text-[10px] text-emerald-600 uppercase font-medium mb-1">Production IPs</p>
                    {cs.known_fixie_ips?.production?.map(ip => <p key={ip} className="text-xs font-mono text-gray-900">{ip}</p>)}
                  </div>
                </div>
              )}
            </CardContent></Card>
            <SaveBar />
          </div>
        )}

        {/* ===== Webhook Configuration ===== */}
        {activeSection === 'webhook-config' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Webhook Configuration</h2>
            <Card className="border-gray-200"><CardContent className="p-5 space-y-3">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-[10px] text-gray-500 uppercase font-medium mb-1">Webhook Endpoint</p>
                <p className="text-sm font-mono text-gray-900">/api/webhooks/assets-mfb</p>
              </div>
              <ConfigField label="Production Webhook Secret" fieldKey="ASSETS_MFB_WEBHOOK_SECRET_PRODUCTION" fields={fields} edits={edits} setEdits={setEdits} sensitive />
            </CardContent></Card>
            <SaveBar />
          </div>
        )}

        {/* ===== Email Configuration ===== */}
        {activeSection === 'email-config' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Email Configuration</h2>
            <Card className="border-gray-200"><CardContent className="p-5 space-y-4">
              <ConfigField label="Resend API Key" fieldKey="RESEND_API_KEY" fields={fields} edits={edits} setEdits={setEdits} sensitive />
              <ConfigField label="From Email Address" fieldKey="SENDER_EMAIL" fields={fields} edits={edits} setEdits={setEdits} />
              <ConfigField label="Support Email Address" fieldKey="SUPPORT_EMAIL" fields={fields} edits={edits} setEdits={setEdits} />
            </CardContent></Card>
            <SaveBar />
          </div>
        )}

        {/* ===== Alert Recipients ===== */}
        {activeSection === 'alert-recipients' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Alert Recipients</h2>
            <Card className="border-gray-200"><CardContent className="p-5 space-y-4">
              {[
                { label: 'Error Alert Email', key: 'ERROR_ALERT_EMAIL', toggle: 'ERROR_ALERTS_ENABLED' },
                { label: 'Daily Summary Email', key: 'DAILY_SUMMARY_EMAIL', toggle: 'DAILY_SUMMARY_ENABLED' },
                { label: 'Velocity Alert Email', key: 'VELOCITY_ALERT_EMAIL', toggle: 'VELOCITY_ALERTS_ENABLED' },
                { label: 'High Value Alert Email', key: 'HIGH_VALUE_ALERT_EMAIL', toggle: 'HIGH_VALUE_ALERTS_ENABLED' },
              ].map(item => (
                <div key={item.key} className="flex items-start gap-3">
                  <div className="flex-1">
                    <ConfigField label={item.label} fieldKey={item.key} fields={fields} edits={edits} setEdits={setEdits} />
                  </div>
                  <div className="pt-6">
                    <button
                      onClick={() => {
                        const current = edits[item.toggle] !== undefined ? edits[item.toggle] : (fields[item.toggle]?.value || 'true');
                        setEdits(prev => ({ ...prev, [item.toggle]: current === 'true' ? 'false' : 'true' }));
                      }}
                      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                        (edits[item.toggle] !== undefined ? edits[item.toggle] : (fields[item.toggle]?.value || 'true')) === 'true'
                          ? 'bg-emerald-500' : 'bg-gray-300'
                      }`}
                      data-testid={`toggle-${item.toggle}`}
                    >
                      <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                        (edits[item.toggle] !== undefined ? edits[item.toggle] : (fields[item.toggle]?.value || 'true')) === 'true'
                          ? 'translate-x-6' : 'translate-x-1'
                      }`} />
                    </button>
                  </div>
                </div>
              ))}
            </CardContent></Card>
            <SaveBar />
          </div>
        )}

        {/* ===== Production Readiness Checklist ===== */}
        {activeSection === 'production-readiness' && cs && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-gray-900">Production Readiness Checklist</h2>
            <Card className="border-gray-200"><CardContent className="p-5">
              <div className="space-y-3">
                {[
                  { label: 'Egress proxy active', ok: cs.proxy_active },
                  { label: 'Outbound IP matches Fixie', ok: cs.ip_matches_fixie },
                  { label: 'MFB base URL from database', ok: cs.url_source === 'DATABASE' },
                  { label: 'Production URL set', ok: fields.ASSETS_MFB_BASE_URL_PRODUCTION?.source !== 'NOT_SET' },
                  { label: 'Production Client ID set', ok: fields.ASSETS_MFB_CLIENT_ID_PRODUCTION?.source !== 'NOT_SET' },
                  { label: 'Production Secret set', ok: fields.ASSETS_MFB_SECRET_PRODUCTION?.source !== 'NOT_SET' },
                  { label: 'Webhook Secret set', ok: fields.ASSETS_MFB_WEBHOOK_SECRET_PRODUCTION?.source !== 'NOT_SET' },
                  { label: 'Resend API Key set', ok: fields.RESEND_API_KEY?.source !== 'NOT_SET' },
                  { label: 'Sender email set', ok: fields.SENDER_EMAIL?.source !== 'NOT_SET' },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3 py-2 border-b border-gray-50 last:border-0">
                    {item.ok ? <CheckCircle className="w-5 h-5 text-emerald-500" /> : <AlertTriangle className="w-5 h-5 text-amber-500" />}
                    <span className={`text-sm ${item.ok ? 'text-gray-900' : 'text-amber-700 font-medium'}`}>{item.label}</span>
                  </div>
                ))}
              </div>
            </CardContent></Card>
          </div>
        )}

        {/* ===== Danger Zone ===== */}
        {activeSection === 'danger-zone' && (
          <div className="space-y-4">
            <h2 className="text-lg font-bold text-red-600">Danger Zone</h2>
            <Card className="border-red-200 bg-red-50/30"><CardContent className="p-5">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-semibold text-gray-900">Reset Config to Defaults</p>
                  <p className="text-xs text-gray-500 mt-0.5">Restore all SystemConfig values to environment variable defaults</p>
                </div>
                <Button variant="outline" size="sm" className="border-red-300 text-red-600 hover:bg-red-100 h-8 text-xs"
                  onClick={() => toast.info('Not available in current version')} data-testid="reset-config-btn">
                  <RefreshCw className="w-3.5 h-3.5 mr-1.5" /> Reset
                </Button>
              </div>
            </CardContent></Card>
          </div>
        )}
      </div>
    </div>
  );
}
